import { manHdrStateCheckboxOption } from "presentation/constant/CheckBox/StaticCheckboxOptions";
import { SupplementaryInvoiceConstant } from "presentation/constant/SupplementaryInvoice/SupplementaryInvoiceConstant";
import { useSupplementaryInvoiceVM } from "presentation/hook/SupplementaryInvoice/useSupplementaryInvoiceVM";
import { useSupplementaryInvoiceTracked } from "presentation/store/SupplementaryInvoice/SupplementaryInvoiceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useMemo } from "react";
import { FieldType, GroupCheckboxList, HPHGroupCheckbox, HPHInputMask, IFieldValue } from "veronica-ui-component/dist/component/core";

const SupplementaryInvoiceSearchForm = () => {
    const [supplementaryInvoiceState] = useSupplementaryInvoiceTracked();
    const SUPPLEMENTARY_INVOICE_CONSTANT = SupplementaryInvoiceConstant.Search;
    const supplementaryInvoiceSearchCriteria = supplementaryInvoiceState.searchCriteria;
    const supplementaryInvoiceVM = useSupplementaryInvoiceVM();

    const memoRefNo = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: "180px", maxWidth: "180px", width: "47.5%" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={supplementaryInvoiceSearchCriteria.refNo || ''}
                fieldValue={supplementaryInvoiceSearchCriteria.refNo}
                fieldLabel={SUPPLEMENTARY_INVOICE_CONSTANT.REF_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'refNo'}
                maxLength={10}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    supplementaryInvoiceVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.REF_NO, supplementaryInvoiceSearchCriteria.refNo, supplementaryInvoiceVM])

    const memoInvoiceCreditNo = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: "180px", maxWidth: "180px", width: "47.5%" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={supplementaryInvoiceSearchCriteria.invoiceCreditNo || ''}
                fieldValue={supplementaryInvoiceSearchCriteria.invoiceCreditNo}
                fieldLabel={SUPPLEMENTARY_INVOICE_CONSTANT.INVOICE_CREDIT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'invoiceCreditNo'}
                maxLength={12}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    supplementaryInvoiceVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.INVOICE_CREDIT_NO, supplementaryInvoiceSearchCriteria.invoiceCreditNo, supplementaryInvoiceVM])

    const memoState = useMemo(() =>
        <div className='im-flex-row-item'>
            <HPHGroupCheckbox
                label={SUPPLEMENTARY_INVOICE_CONSTANT.STATE}
                checkboxData={manHdrStateCheckboxOption}
                selectedValues={supplementaryInvoiceSearchCriteria.states}
                orientation={"horizontal"}
                onChange={(e: (GroupCheckboxList | undefined)[]) => supplementaryInvoiceVM.onGroupCheckboxChange(e, supplementaryInvoiceSearchCriteria, "states")}
            />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.STATE, supplementaryInvoiceSearchCriteria, supplementaryInvoiceVM])

    const memoBillToCompany = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: "180px", maxWidth: "390px", width: "100%" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={supplementaryInvoiceSearchCriteria?.billToCompanies?.join(',')}
                fieldValue={supplementaryInvoiceSearchCriteria?.billToCompanies}
                fieldLabel={SUPPLEMENTARY_INVOICE_CONSTANT.BILL_TO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompanies'}
                maxLength={60}
                isMultipleDropDown={true}
                options={supplementaryInvoiceState.dynamicOptions.billToCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    supplementaryInvoiceVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.BILL_TO_COMPANY, supplementaryInvoiceSearchCriteria.billToCompanies, supplementaryInvoiceState.dynamicOptions.billToCompDropdownOptions, supplementaryInvoiceVM])


    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "180px", maxWidth: "390px", width: "100%" }}>
            <DateRangePickerComponent
                label={SUPPLEMENTARY_INVOICE_CONSTANT.OPS_DATE_RANGE}
                width='100%'
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: supplementaryInvoiceSearchCriteria.opsDateFrom, endDate: supplementaryInvoiceSearchCriteria.opsDateTo }}
                onDatesChange={supplementaryInvoiceVM.onDateRangeChange}
            />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.OPS_DATE_RANGE, supplementaryInvoiceSearchCriteria.opsDateFrom, supplementaryInvoiceSearchCriteria.opsDateTo, supplementaryInvoiceVM.onDateRangeChange])

    const memoCreatedDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "180px", maxWidth: "390px", width: "100%" }}>
            <DateRangePickerComponent
                label={SUPPLEMENTARY_INVOICE_CONSTANT.CREATED_DATE_RANGE}
                width='100%'
                fieldNames={{ startField: "createdDateFrom", endField: "createdDateTo" }}
                dateRange={{ startDate: supplementaryInvoiceSearchCriteria.createdDateFrom, endDate: supplementaryInvoiceSearchCriteria.createdDateTo }}
                onDatesChange={supplementaryInvoiceVM.onDateRangeChange}
            />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.CREATED_DATE_RANGE, supplementaryInvoiceSearchCriteria.createdDateFrom, supplementaryInvoiceSearchCriteria.createdDateTo, supplementaryInvoiceVM.onDateRangeChange])

    const memoCoVslVoy = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: "180px", maxWidth: "390px", width: "100%" }}>
            <HPHInputMask
                width='100%'
                label={SUPPLEMENTARY_INVOICE_CONSTANT.CO_VSL_VOY}
                value={{
                    co: supplementaryInvoiceSearchCriteria.co ?? '',
                    vsl: supplementaryInvoiceSearchCriteria.vsl ?? '',
                    voy: supplementaryInvoiceSearchCriteria.voy ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 6, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => supplementaryInvoiceVM.onSearchCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy'
                })} />
        </div>
        , [SUPPLEMENTARY_INVOICE_CONSTANT.CO_VSL_VOY, supplementaryInvoiceSearchCriteria.co, supplementaryInvoiceSearchCriteria.vsl, supplementaryInvoiceSearchCriteria.voy, supplementaryInvoiceVM])

    return <>

        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.states ? memoState : null}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.coVslVoy ? memoCoVslVoy : null}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.refNo ? memoRefNo : null}
            {supplementaryInvoiceState.enabledSearchCriteria.invoiceCreditNo ? memoInvoiceCreditNo : null}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.billToCompanies ? memoBillToCompany : null}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.opsDateRange ? memoOpsDateFromTo : null}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {supplementaryInvoiceState.enabledSearchCriteria.createdDateRange ? memoCreatedDateFromTo : null}
        </CriteriaItemContainer>

    </>;
}

export default memo(SupplementaryInvoiceSearchForm);